/**
 * Le tunnel Fixer Lite parle à `/v2`, et à rien d'autre du front.
 *
 * Pourquoi `fetch` et non `src/utils/api` : le transport XHR de ce module
 * (`xhr.onload = () => resolve(safeParse())`) résout le corps parsé QUEL QUE
 * SOIT le statut HTTP. Une 422 de validation, un 401 sans jeton et une réponse
 * réussie reviennent tous comme un objet, et rien ne les distingue. Or ce
 * tunnel vit de ces trois cas : le 401 décide s'il faut demander une connexion,
 * la 422 nomme le champ fautif sous le champ. C'est exactement le piège que
 * FIX-542 a corrigé dans `src/annuaire/resolveClient.js`, pour la même raison.
 *
 * Les routes consommées ici sont livrées par FIXERLIGHT-017 (api#799) et ne
 * changent pas : seul leur consommateur change (FIXERLIGHT-020).
 */
import { API_BASE_URL } from '../../utils/apiBaseUrl';

const TIMEOUT_MS = 8000;

/** Levée quand l'api répond un statut d'erreur, avec de quoi le raconter. */
export class V2Error extends Error {
  constructor(status, body) {
    super(`[v2] ${status}`);
    this.name = 'V2Error';
    this.status = status;
    /**
     * Erreurs par champ d'une 422 Laravel (`{message, errors: {champ: [...]}}`),
     * ou objet vide. L'écran les affiche sous le champ nommé plutôt que dans un
     * bandeau générique.
     */
    this.fieldErrors = (body && body.errors) || {};
    /**
     * Le refus métier stable d'un `{ error: { code, message, details } }`
     * (`code_invalid`, `slot_taken`…), ou `null`. `details` porte de quoi
     * l'expliquer à l'écran (`attempts_left`, `retry_after_seconds`).
     */
    this.code = (body && body.error && body.error.code) || null;
    this.details = (body && body.error && body.error.details) || {};
    this.body = body || null;
  }
}

/** `true` quand la demande a été refusée faute de compte client identifié. */
export const isUnauthenticated = (error) =>
  error instanceof V2Error && error.status === 401;

/**
 * Le jeton maison du client RJ, celui que `ResolveRjClient` relit côté api.
 *
 * `loadStore()` ne persiste que `client.id` et `client.token` — c'est la seule
 * source, et elle vit dans `localStorage`. Absente au rendu serveur, d'où la
 * garde : le SSR ne poste jamais de demande.
 */
const bearer = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = JSON.parse(window.localStorage.getItem('state') || 'null');
    const token = stored && stored.client && stored.client.token;
    return typeof token === 'string' && token !== '' ? token : null;
  } catch (error) {
    // localStorage refusé (navigation privée, site data bloqué) : pas de jeton,
    // donc pas de demande — jamais une exception qui casserait le rendu.
    return null;
  }
};

const timedFetch = (url, options) => {
  if (typeof AbortController === 'undefined') return fetch(url, options);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  return fetch(url, { ...options, signal: controller.signal }).then(
    (response) => {
      clearTimeout(timer);
      return response;
    },
    (error) => {
      clearTimeout(timer);
      throw error;
    },
  );
};

async function call(path, { method = 'GET', body = null, auth = false } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== null) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = bearer();
    // Pas de jeton : on le dit tout de suite plutôt que de laisser partir une
    // requête dont on connaît déjà la réponse. L'écran en fait un appel à se
    // connecter, pas un message d'échec.
    if (token === null) throw new V2Error(401, null);
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await timedFetch(`${API_BASE_URL}/v2${path}`, {
    method,
    headers,
    ...(body !== null ? { body: JSON.stringify(body) } : {}),
  });

  // Un corps illisible sur une réponse d'erreur ne doit pas masquer le statut :
  // c'est le statut qui porte la décision.
  const parsed = await response.json().catch(() => null);

  if (!response.ok) throw new V2Error(response.status, parsed);

  return parsed;
}

/**
 * La fiche publique du réparateur : nom, prestations, zone, instantané.
 * Adressée par l'ID de la fiche annuaire, jamais par `url_path` — il contient
 * des « / » (cf. le commentaire de `routes/v2.php`).
 */
export const fetchPublicShop = (shopId) =>
  call(`/public/shops/${shopId}`).then((json) => json.data);

/**
 * Le prochain horaire libre de N ateliers en un seul appel (contrat
 * FIXERLIGHT-055, `docs/specs/fixerlight-055-listing-prochain-horaire.md`
 * du dépôt api) : ce que le listing d'une ville affiche par carte
 * (FIXERLIGHT-084), sans rejouer `fetchAvailability` une fois par atelier.
 *
 * Réponse : `{ data: [{ shop_id, next_slot }] }`, un TABLEAU CREUX — un id
 * sans compte Fixer Lite, ou sans aucun créneau dans les 21 jours, est
 * simplement absent (jamais un 404 partiel, § 1 du contrat). Rendue ici sous
 * forme d'objet indexé par `shop_id`, plus pratique pour la carte qu'un
 * tableau à reparcourir.
 *
 * Anonyme (route publique) : pas de `auth: true`, comme `fetchAvailability`.
 * Aucune requête si `shopIds` est vide, pour ne jamais poster
 * `?` sans paramètre (l'api la refuserait en 422, § 3 du contrat).
 *
 * @param {Array<number|string>} shopIds
 * @return {Promise<Object<number, string>>} next_slot ISO 8601 Zulu, par shop_id.
 */
export const fetchNextSlots = (shopIds) => {
  const ids = [
    ...new Set(
      (shopIds || []).map(Number).filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];
  if (ids.length === 0) return Promise.resolve({});

  const query = ids
    .map((id) => `shop_ids[]=${encodeURIComponent(id)}`)
    .join('&');

  return call(`/public/shops/next-slots?${query}`).then((json) =>
    Object.fromEntries(
      (json.data || [])
        .filter((entry) => entry && entry.shop_id != null)
        .map((entry) => [entry.shop_id, entry.next_slot]),
    ),
  );
};

/**
 * Les créneaux proposables entre deux dates, au format `YYYY-MM-DD`.
 * Réponse : `{ duration_min, slots: string[] }`, les heures de début libres
 * en ISO 8601 Zulu (voir `creneaux.js`).
 *
 * `serviceKey` accepte soit une chaîne (une prestation, `&service=`), soit
 * un tableau (plusieurs prestations D16, `&services[]=`) — c'est le même
 * couple de champs que `PublicBookingController::availability()`
 * (`service`/`services`, mutuellement exclusifs). Un tableau sert le
 * déplacement d'un rendez-vous existant (FIXERLIGHT-052 lot 2) : la durée
 * rendue est celle des prestations ACTUELLES du compte pour ces clés
 * (`durationFor`), pas forcément la durée réellement retenue sur ce
 * rendez-vous — elle peut avoir changé depuis (voir
 * `workshopBookingReschedule.js`, qui compare les deux avant d'afficher des
 * créneaux).
 */
export const fetchAvailability = (shopId, from, to, serviceKey) => {
  let service = '';
  if (Array.isArray(serviceKey)) {
    service = serviceKey
      .filter(Boolean)
      .map((key) => `&services[]=${encodeURIComponent(key)}`)
      .join('');
  } else if (serviceKey) {
    service = `&service=${encodeURIComponent(serviceKey)}`;
  }
  return call(
    `/public/shops/${shopId}/availability?from=${from}&to=${to}${service}`,
  ).then((json) => json.data);
};

/**
 * La surface de la réservation depuis le site du réparateur (`/reserver/:slug`,
 * FIXERLIGHT-056) : adressée par le slug du compte atelier, sans jeton. Le
 * client n'a pas de compte RJ. Ce sont les routes `…/site` (source SITE), pas
 * les routes nues `…/{slug}` (source DIRECT, réservées à la recette) : seules
 * les premières savent confirmer un horaire après un code par email.
 * Elles répondent 404 tant que `FIXER_LITE_SITE_ENABLED` est bas côté api.
 */
const parSlug = (slug) => `/public/${encodeURIComponent(slug)}`;

export const fetchPublicSlug = (slug) =>
  call(`${parSlug(slug)}/site`).then((json) => json.data);

export const fetchAvailabilitySlug = (slug, from, to, serviceKey) => {
  const service = serviceKey
    ? `&service=${encodeURIComponent(serviceKey)}`
    : '';
  return call(
    `${parSlug(slug)}/availability?from=${from}&to=${to}${service}`,
  ).then((json) => json.data);
};

/**
 * Les mêmes lectures d'adresse et de couverture que par la fiche
 * (`suggestAddresses`, `fetchAddress`, `fetchCoverage`), adressées par le
 * slug : la page `/reserver/:slug` ne connaît pas l'identifiant de la fiche.
 * Routes nues (`public/{slug}/…`, pas `…/site`) : l'api n'en a pas d'autres, et
 * elles ne créent rien.
 */
export const suggestAddressesSlug = (slug, texte, session) =>
  call(
    `${parSlug(slug)}/addresses/autocomplete?q=${encodeURIComponent(
      texte,
    )}&session=${encodeURIComponent(session)}`,
  ).then((json) => json.data);

export const fetchAddressSlug = (slug, placeId, session) =>
  call(
    `${parSlug(slug)}/addresses/${encodeURIComponent(
      placeId,
    )}?session=${encodeURIComponent(session)}`,
  ).then((json) => json.data);

export const fetchCoverageSlug = (slug, latitude, longitude) =>
  call(
    `${parSlug(slug)}/coverage?latitude=${encodeURIComponent(
      latitude,
    )}&longitude=${encodeURIComponent(longitude)}`,
  ).then((json) => json.data);

/**
 * La demande. En instantané, la réponse porte `instant_code_required: true` et
 * un `public_token` : le client saisit alors le code reçu par email
 * (`confirmCodeSlug`) avant que l'horaire soit confirmé.
 */
export const postBookingSlug = (slug, body) =>
  call(`${parSlug(slug)}/site/bookings`, { method: 'POST', body }).then(
    (json) => json.data,
  );

const parJeton = (slug, token) =>
  `${parSlug(slug)}/site/bookings/${encodeURIComponent(token)}`;

/**
 * La demande, relue par son jeton pour la page « Créer mon espace » : de quoi
 * pré-remplir l'inscription (`customer: { name, email, phone }`) et nommer le
 * rendez-vous (`workshop: { name }`, `status`, `scheduled_at`). 404 quand le
 * jeton est inconnu, déjà utilisé ou périmé : la page s'ouvre alors en version
 * générique. Route à livrer côté api (FIXERLIGHT-064).
 */
export const fetchDemandeSite = (slug, token) =>
  call(parJeton(slug, token)).then((json) => json.data);

/** Valide le code à 6 chiffres. 200 : rendez-vous confirmé. */
export const confirmCodeSlug = (slug, token, code) =>
  call(`${parJeton(slug, token)}/confirm-code`, {
    method: 'POST',
    body: { code },
  }).then((json) => json && json.data);

/** Envoie un nouveau code (60 s minimum entre deux envois). */
export const resendCodeSlug = (slug, token) =>
  call(`${parJeton(slug, token)}/resend-code`, { method: 'POST' }).then(
    (json) => json && json.data,
  );

/**
 * Le questionnaire d'auto-diagnostic courant. Public et sans auth : le tunnel
 * le rend avant toute connexion, c'est de la configuration d'affichage.
 */
export const fetchQuestionnaire = () =>
  call('/diagnostic-questionnaire').then((json) => json.data);

/**
 * Les suggestions d'adresse, servies par l'atelier visé (FIXERLIGHT-048) :
 * préférées autour de son point de départ, et bornées à une fiche réelle.
 * `session` est le jeton de session Places (UUID) que le détail reprendra :
 * Google facture la session entière, pas chaque frappe.
 */
export const suggestAddresses = (shopId, texte, session) =>
  call(
    `/public/shops/${shopId}/addresses/autocomplete?q=${encodeURIComponent(
      texte,
    )}&session=${encodeURIComponent(session)}`,
  ).then((json) => json.data);

/**
 * Le détail d'une suggestion choisie : `{ place_id, formatted_address,
 * latitude, longitude }`. C'est ce qui SITUE l'adresse (FIXERLIGHT-073) : un
 * atelier avec zone refuse une adresse sans position.
 */
export const fetchAddress = (shopId, placeId, session) =>
  call(
    `/public/shops/${shopId}/addresses/${encodeURIComponent(
      placeId,
    )}?session=${encodeURIComponent(session)}`,
  ).then((json) => json.data);

/**
 * La lecture de couverture (FIXERLIGHT-048), une fois l'adresse SITUÉE
 * (FIXERLIGHT-073) : « suis-je couvert, à quelle distance, pour quel
 * supplément ». `{ covered, distance_km, ring_index, surcharge_eur,
 * max_radius_km }`. Ne crée rien, n'écrit rien : un compte sans couverture
 * rend `covered: true` et des null, sans cas particulier à écrire ici
 * (FIXERLIGHT-066).
 */
export const fetchCoverage = (shopId, latitude, longitude) =>
  call(
    `/public/shops/${shopId}/coverage?latitude=${encodeURIComponent(
      latitude,
    )}&longitude=${encodeURIComponent(longitude)}`,
  ).then((json) => json.data);

/**
 * Poser la demande. Exige le compte RJ du client : `client_id` vient du jeton
 * résolu côté api, et de RIEN d'autre — aucun champ du corps ne le porte.
 */
export const postBooking = (shopId, body) =>
  call(`/public/shops/${shopId}/bookings`, {
    method: 'POST',
    body,
    auth: true,
  }).then((json) => json.data);
